import {
  AuthFooterLink,
  AuthMessage,
  AuthPasswordInput,
  AuthSubmitButton,
  Button,
} from '@protoss/ui';
import { verifyPasswordResetCode } from 'firebase/auth';
import * as React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AuthCard, AuthHeader, AuthLayout } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseAuth } from '../../lib/firebase';
import { getPreAuthConfig } from '../../lib/preAuthConfig';
import { withDevWorkspaceParam } from '../../lib/url';
import './auth-pages.css';

/**
 * Reset Password Page
 *
 * Lands here from a Firebase password reset email (via /auth/action, which
 * appends the oobCode). Validates the code on mount, then sets the new password
 * with AuthContext.resetPassword (Firebase confirmPasswordReset). Invite links
 * use /auth/set-password instead.
 */

function firebaseErrorCode(error: unknown): string {
  return (error as { code?: string } | null)?.code ?? '';
}

interface FormData {
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

type PageState = 'validating' | 'valid' | 'invalid' | 'expired';
type FormState = 'idle' | 'loading' | 'success' | 'error';

export function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  // Page state
  const [pageState, setPageState] = React.useState<PageState>('validating');
  const [formState, setFormState] = React.useState<FormState>('idle');

  // Form state
  const [formData, setFormData] = React.useState<FormData>({
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = React.useState<FormErrors>({});
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState<string | null>(null);

  // Validate the reset code on mount (does not consume it).
  React.useEffect(() => {
    if (!oobCode) {
      setPageState('invalid');
      return;
    }

    let cancelled = false;
    verifyPasswordResetCode(firebaseAuth, oobCode)
      .then((resolvedEmail) => {
        if (cancelled) return;
        setEmail(resolvedEmail);
        setPageState('valid');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPageState(
          firebaseErrorCode(error) === 'auth/expired-action-code' ? 'expired' : 'invalid'
        );
      });

    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  if (!getPreAuthConfig().allowedMethods.includes('password')) {
    return <Navigate to="/signin" replace />;
  }

  // Validation
  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return undefined;
  };

  const validateConfirmPassword = (
    password: string,
    confirmPassword: string
  ): string | undefined => {
    if (!confirmPassword) {
      return 'Please confirm your password';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(formData.password, formData.confirmPassword),
    };

    setErrors(newErrors);
    return !newErrors.password && !newErrors.confirmPassword;
  };

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Also clear confirm password error if user is typing in password field
    if (name === 'password' && errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }

    // Clear general error when user modifies form
    if (generalError) {
      setGeneralError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Clear previous errors
    setGeneralError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    if (!oobCode) {
      setPageState('invalid');
      return;
    }

    setFormState('loading');

    try {
      await resetPassword(oobCode, formData.password);
      setFormState('success');
    } catch (error) {
      const code = firebaseErrorCode(error);
      if (code === 'auth/expired-action-code') {
        setPageState('expired');
        return;
      }
      if (code === 'auth/invalid-action-code') {
        setPageState('invalid');
        return;
      }
      setFormState('error');
      if (code === 'auth/weak-password') {
        setErrors({ password: 'Please choose a stronger password' });
      } else if (code === 'auth/network-request-failed') {
        setGeneralError('Unable to reach the server. Check your connection and try again.');
      } else {
        console.error('Password reset failed', error);
        setGeneralError('Unable to reset password. Please try again.');
      }
    }
  };

  const isLoading = formState === 'loading';
  const isSuccess = formState === 'success';

  // Validating token state
  if (pageState === 'validating') {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader title="Reset your password" />
          <div className="auth-form-container">
            <AuthMessage variant="info">Validating reset link...</AuthMessage>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  // Invalid token state
  if (pageState === 'invalid') {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader title="Reset link expired" />

          <div className="auth-form-container">
            <AuthMessage variant="error">
              This password reset link is invalid or has expired. Please request a new one.
            </AuthMessage>

            <div className="auth-form">
              <Button
                variant="primary"
                size="md"
                onClick={() => (window.location.href = withDevWorkspaceParam('/forgot-password'))}
              >
                Request new reset link
              </Button>
            </div>
          </div>

          <div className="auth-footer-text">
            <AuthFooterLink href={withDevWorkspaceParam('/signin')}>Back to sign in</AuthFooterLink>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  // Expired token state (could be same as invalid, but allows for different messaging)
  if (pageState === 'expired') {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader title="Reset link expired" />

          <div className="auth-form-container">
            <AuthMessage variant="error">
              This password reset link has expired. For security reasons, reset links are only valid
              for a limited time.
            </AuthMessage>

            <div className="auth-form">
              <Button
                variant="primary"
                size="md"
                onClick={() => (window.location.href = withDevWorkspaceParam('/forgot-password'))}
              >
                Request new reset link
              </Button>
            </div>
          </div>

          <div className="auth-footer-text">
            <AuthFooterLink href={withDevWorkspaceParam('/signin')}>Back to sign in</AuthFooterLink>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader title="Password updated" />

          <div className="auth-form-container">
            <AuthMessage variant="success">
              Your password has been reset successfully. You can now sign in with your new password.
            </AuthMessage>

            <div className="auth-form">
              <Button
                variant="primary"
                size="md"
                onClick={() => (window.location.href = withDevWorkspaceParam('/signin'))}
              >
                Go to sign in
              </Button>
            </div>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  // Form state (valid token, user entering new password)
  return (
    <AuthLayout>
      <AuthCard>
        <AuthHeader
          title="Reset your password"
          subtitle={email ? `Choose a new password for ${email}` : undefined}
        />

        <div className="auth-form-container">
          {generalError && <AuthMessage variant="error">{generalError}</AuthMessage>}

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            <AuthPasswordInput
              name="password"
              label="New Password"
              placeholder="Enter new password"
              value={formData.password}
              onChange={handleInputChange}
              error={errors.password}
              helperText={!errors.password ? 'Must be at least 8 characters' : undefined}
              autoComplete="new-password"
              disabled={isLoading}
            />

            <AuthPasswordInput
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Re-enter new password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              error={errors.confirmPassword}
              autoComplete="new-password"
              disabled={isLoading}
            />

            <AuthSubmitButton isLoading={isLoading}>Reset password</AuthSubmitButton>
          </form>
        </div>

        <div className="auth-footer-text">
          Remember your password?{' '}
          <AuthFooterLink href={withDevWorkspaceParam('/signin')}>Sign in</AuthFooterLink>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
